import React, { useState } from 'react';
import { config } from '../config/environment';

interface HeightInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (height: number) => void;
  onError?: (error: string) => void;
}

export const HeightInputModal: React.FC<HeightInputModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
}) => {
  const [height, setHeight] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!height || parseFloat(height) <= 0) {
      setError('신장을 입력해주세요');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('인증 정보가 없습니다');
      }

      const heightValue = parseFloat(height);

      const response = await fetch(`${config.apiUrl}/user/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ height: heightValue }),
      });

      if (!response.ok) {
        throw new Error('신장 정보 저장 실패');
      }

      const data = await response.json();
      if (data.success) {
        onSuccess?.(heightValue);
        setHeight('');
        onClose();
      } else {
        throw new Error(data.message || '신장 정보 저장 실패');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '신장 정보 저장 실패';
      setError(errorMessage);
      onError?.(errorMessage);
      console.error('[HeightInputModal] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
        {/* 헤더 */}
        <h2 className="text-xl font-bold text-gray-900 mb-2">신장 정보 입력</h2>
        <p className="text-sm text-gray-600 mb-6">
          BMI를 자동 계산하기 위해 신장 정보가 필요합니다.
        </p>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              신장 (cm) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="예: 170"
              min="0"
              max="300"
              step="0.1"
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              autoFocus
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* 버튼 */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HeightInputModal;
